import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Alert from "../../../Common/Alert";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import useAlertFromLocation from "../../../../hooks/useAlertFromLocation";
import useDeleteData from "../../../../hooks/useDeleteData";
import ReactDataTable from "../../../Common/ReactDataTable";
import {
  GREEN,
  JournalVoucherStyles,
  NAVY,
  RED,
  TYPE_LABELS,
  money,
  toInput,
} from "./JournalVoucherShared";

const ALL = "all";
// Lines carrying no costing dimension, which is a case worth filtering to on
// its own when someone is chasing down unallocated postings.
const NONE = "none";

// The ranges an accountant actually asks for, so the common case is one click
// instead of two date pickers.
const quickRanges = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();

  return [
    {
      label: "This month",
      from: toInput(new Date(year, month, 1)),
      to: toInput(today),
    },
    {
      label: "Last month",
      from: toInput(new Date(year, month - 1, 1)),
      to: toInput(new Date(year, month, 0)),
    },
    {
      label: "This quarter",
      from: toInput(new Date(year, Math.floor(month / 3) * 3, 1)),
      to: toInput(today),
    },
    {
      label: "This year",
      from: toInput(new Date(year, 0, 1)),
      to: toInput(today),
    },
  ];
};

// The subsidiary ledger hangs off a different relation per type, so the label
// has to be assembled from whichever one the line actually points at.
const subCategoryLabel = (line) => {
  const name = line.sub_category?.name;

  if (!name) return "";
  if (name === "new") return line.sub_category.new_name || "";
  if (name === "hr") return line.hr ? line.hr.card_no + " - " + line.hr.name : "";
  if (name === "assets")
    return line.asset ? line.asset.code + " - " + line.asset.name : "";
  if (name === "supplier")
    return line.supplier ? line.supplier.code + " - " + line.supplier.name : "";
  if (name === "distributor")
    return line.distributor
      ? line.distributor.code + " - " + line.distributor.name
      : "";

  return name;
};

const costCenterLabel = (line) =>
  line.cost_center ? line.cost_center.code + " - " + line.cost_center.name : "";

// created_by is recorded per line, so the voucher's author is the author of its
// first line.
const voucherAuthor = (voucher) => voucher.items?.[0]?.created_by?.name || "";

const typeBody = (row) =>
  row.type ? (
    <span className={"jv-badge jv-badge-" + row.type_key}>{row.type}</span>
  ) : (
    ""
  );

// An empty side of the entry reads better as a dash than as a blank cell; the
// exported value stays empty either way.
const amountBody = (row, field) =>
  row[field] ? row[field] : <span className="jv-nil">&ndash;</span>;

const List = () => {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);

  const ranges = useMemo(quickRanges, []);
  // The date range is the only filter the server applies; everything below it
  // narrows the range that is already loaded, so it responds without a refetch.
  const [fromDate, setFromDate] = useState(ranges[0].from);
  const [toDate, setToDate] = useState(ranges[0].to);
  const [jvNo, setJvNo] = useState("");
  const [type, setType] = useState(ALL);
  const [costCenter, setCostCenter] = useState(ALL);
  const [createdBy, setCreatedBy] = useState(ALL);

  const fetchData = async (from = "", to = "") => {
    setLoading(true);
    try {
      let url = "/journal-voucher";
      const params = new URLSearchParams();
      if (from) params.append("from_date", from);
      if (to) params.append("to_date", to);
      if (params.toString()) url += `?${params.toString()}`;

      const res = await Http.get(url);
      setVouchers(res.data.list || []);
    } catch (error) {
      console.error("Fetch error:", error);
      setVouchers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(fromDate, toDate);
  }, []);

  // Handle date filtering
  const handleDateFilter = () => {
    fetchData(fromDate, toDate);
  };

  const applyRange = (range) => {
    setFromDate(range.from);
    setToDate(range.to);
    fetchData(range.from, range.to);
  };

  // Every dropdown is built from what the loaded range actually contains, with
  // counts, so no option can be picked that returns nothing.
  const options = useMemo(() => {
    const types = new Map();
    const authors = new Map();
    const centers = new Map();

    vouchers.forEach((voucher) => {
      types.set(voucher.type, (types.get(voucher.type) || 0) + 1);

      const author = voucherAuthor(voucher);
      if (author) authors.set(author, (authors.get(author) || 0) + 1);

      (voucher.items || []).forEach((line) => {
        const key = line.acc_cost_center_id
          ? String(line.acc_cost_center_id)
          : NONE;
        const label =
          key === NONE ? "Not assigned" : costCenterLabel(line) || key;
        const current = centers.get(key);
        centers.set(key, { label, count: (current?.count || 0) + 1 });
      });
    });

    const byLabel = (a, b) => a.label.localeCompare(b.label);

    return {
      types: [...types].map(([value, count]) => ({
        value,
        label: TYPE_LABELS[value] || value,
        count,
      })),
      authors: [...authors]
        .map(([value, count]) => ({ value, label: value, count }))
        .sort(byLabel),
      // "Not assigned" sorts last: it is a fallback, not a cost center.
      centers: [...centers]
        .map(([value, meta]) => ({ value, ...meta }))
        .sort((a, b) =>
          a.value === NONE ? 1 : b.value === NONE ? -1 : byLabel(a, b)
        ),
    };
  }, [vouchers]);

  // One row per journal line. Voucher-level filters drop the whole voucher; the
  // cost center filter is line-level, so it keeps only the lines charged to that
  // center.
  const { rows, debitTotal, creditTotal, voucherCount, lineCount } = useMemo(() => {
    const term = jvNo.trim().toLowerCase();
    const result = [];
    let debit = 0;
    let credit = 0;
    let count = 0;
    let lines_total = 0;

    vouchers.forEach((voucher) => {
      if (type !== ALL && voucher.type !== type) return;
      if (createdBy !== ALL && voucherAuthor(voucher) !== createdBy) return;
      if (term && !String(voucher.serial_number || "").toLowerCase().includes(term))
        return;

      const lines = (voucher.items || []).filter((line) => {
        if (costCenter === ALL) return true;
        if (costCenter === NONE) return !line.acc_cost_center_id;
        return String(line.acc_cost_center_id) === costCenter;
      });

      if (lines.length === 0) return;
      count += 1;
      lines_total += lines.length;

      // The voucher's own head row. Its debit and credit are the voucher total
      // held on acc_journal_voucher, not a sum of the lines below it, so it
      // still states what the voucher is worth when a line-level filter is
      // showing only part of it.
      const voucherTotal = parseFloat(voucher.total_amount) || 0;
      result.push({
        id: "voucher-" + voucher.id,
        journal_voucher_id: voucher.id,
        isVoucherHead: true,
        date: voucher.date,
        serial_number: voucher.serial_number,
        type: TYPE_LABELS[voucher.type] || voucher.type,
        type_key: voucher.type,
        created_by: voucherAuthor(voucher),
        chart_of_account: voucher.remarks || "",
        cost_center: "",
        sub_category: "",
        bill_number: "",
        bill_date: "",
        reference: "",
        debit: money(voucherTotal),
        credit: money(voucherTotal),
        debit_value: voucherTotal,
        credit_value: voucherTotal,
      });

      lines.forEach((line) => {
        const lineDebit = parseFloat(line.debit) || 0;
        const lineCredit = parseFloat(line.credit) || 0;
        debit += lineDebit;
        credit += lineCredit;

        // The voucher-level columns stay empty on the lines: the head row above
        // already carries them. Amounts are formatted here so the CSV/PDF
        // export carries the same text the table shows.
        result.push({
          id: line.id,
          journal_voucher_id: voucher.id,
          isVoucherHead: false,
          date: "",
          serial_number: "",
          type: "",
          type_key: voucher.type,
          created_by: "",
          chart_of_account:
            line.chart_of_account?.code + " - " + line.chart_of_account?.name,
          cost_center: costCenterLabel(line),
          sub_category: subCategoryLabel(line),
          bill_number: line.bill_number,
          bill_date: line.bill_date,
          // One column for both: a line opens a reference or posts against one.
          reference: line.reference || line.existing_reference || "",
          debit: lineDebit > 0 ? money(lineDebit) : "",
          credit: lineCredit > 0 ? money(lineCredit) : "",
          debit_value: lineDebit,
          credit_value: lineCredit,
        });
      });
    });

    return {
      rows: result,
      debitTotal: debit,
      creditTotal: credit,
      voucherCount: count,
      lineCount: lines_total,
    };
  }, [vouchers, type, createdBy, costCenter, jvNo]);

  // to delete data call
  const deleteData = useDeleteData(() => fetchData(fromDate, toDate));
  // to show alert message
  const alert = useAlertFromLocation();

  const filtered =
    jvNo.trim() !== "" ||
    type !== ALL ||
    costCenter !== ALL ||
    createdBy !== ALL;

  const clearFilters = () => {
    setJvNo("");
    setType(ALL);
    setCostCenter(ALL);
    setCreatedBy(ALL);
  };

  // Totals that disagree mean the selection does not balance. Under a
  // line-level filter that is expected, so it is only flagged as a problem when
  // whole vouchers are in view.
  const difference = debitTotal - creditTotal;
  const balanced = Math.abs(difference) < 0.005;
  const partialSelection = costCenter !== ALL;

  // columns for the datatable. Sorting is off across the board: the grid is a
  // master/detail list, and any reorder would cut the head rows away from their
  // own lines.
  const cols = [
    { field: "date", header: "Date", sortable: false },
    { field: "serial_number", header: "JV No", sortable: false },
    { field: "type", header: "Type", sortable: false, body: typeBody },
    {
      field: "chart_of_account",
      header: "Chart Of Accounts",
      sortable: false,
      bodyClassName: "jv-account",
    },
    { field: "cost_center", header: "Cost Center", sortable: false },
    { field: "sub_category", header: "Sub Category", sortable: false },
    { field: "bill_number", header: "Bill No", sortable: false },
    { field: "bill_date", header: "Bill Date", sortable: false },
    { field: "reference", header: "Reference", sortable: false },
    {
      field: "debit",
      header: "Debit",
      sortable: false,
      className: "jv-num",
      headerClassName: "jv-num",
      body: (row) => amountBody(row, "debit"),
    },
    {
      field: "credit",
      header: "Credit",
      sortable: false,
      className: "jv-num",
      headerClassName: "jv-num",
      body: (row) => amountBody(row, "credit"),
    },
    { field: "created_by", header: "Created By", sortable: false },
  ];

  return (
    <div>
      <IsLogin></IsLogin>
      <JournalVoucherStyles />
      <div>
        <div className="am-mainpanel">
          <div className="am-pagetitle">
            <div>
              <h5 className="am-title mb-1">Journal Vouchers</h5>
              <p className="text-muted mb-0 tx-12">
                Double-entry postings across all journal types, by date range
              </p>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary"
                onClick={() => fetchData(fromDate, toDate)}
                title="Reload the current range"
              >
                <i className="fa fa-refresh"></i>
              </button>
              <Link to="/create-journal-voucher" className="btn btn-primary">
                <i className="fa fa-plus-square mg-r-6"></i>New Voucher
              </Link>
            </div>
          </div>
          <div className="am-pagebody">
            <Alert alert={alert} />

            {/* Filters */}
            <div className="jv-panel mb-4">
              <div className="jv-panel-body">
                <div className="row align-items-end g-3">
                  <div className="col-md-3">
                    <label className="jv-label">From Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="jv-label">To Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-6 d-flex flex-wrap align-items-center gap-2">
                    <button
                      className="btn btn-primary"
                      onClick={handleDateFilter}
                    >
                      <i className="fa fa-search me-1"></i>Apply
                    </button>
                    {ranges.map((range) => (
                      <button
                        key={range.label}
                        type="button"
                        className={
                          "jv-chip" +
                          (fromDate === range.from && toDate === range.to
                            ? " active"
                            : "")
                        }
                        onClick={() => applyRange(range)}
                      >
                        {range.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="jv-divider"></div>

                <div className="row align-items-end g-3">
                  <div className="col-lg-3 col-md-6">
                    <label className="jv-label">Journal No.</label>
                    <div className="input-group">
                      <span className="input-group-text bg-light">
                        <i className="fa fa-hashtag text-muted"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by JV number"
                        value={jvNo}
                        onChange={(e) => setJvNo(e.target.value)}
                      />
                      {jvNo && (
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={() => setJvNo("")}
                          title="Clear"
                        >
                          <i className="fa fa-times"></i>
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="jv-label">Type</label>
                    <select
                      className="form-control"
                      value={type}
                      onChange={(e) => setType(e.target.value)}
                    >
                      <option value={ALL}>
                        All types ({vouchers.length})
                      </option>
                      {options.types.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label} ({option.count})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="jv-label">Cost Center</label>
                    <select
                      className="form-control"
                      value={costCenter}
                      onChange={(e) => setCostCenter(e.target.value)}
                    >
                      <option value={ALL}>All cost centers</option>
                      {options.centers.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label} ({option.count})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-lg-3 col-md-6">
                    <label className="jv-label">Created By</label>
                    <select
                      className="form-control"
                      value={createdBy}
                      onChange={(e) => setCreatedBy(e.target.value)}
                    >
                      <option value={ALL}>Anyone</option>
                      {options.authors.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label} ({option.count})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {filtered && (
                  <div className="d-flex flex-wrap align-items-center gap-3 mt-3 jv-active-filters">
                    <span>
                      <i className="fa fa-filter me-1"></i>
                      Showing <strong>{voucherCount}</strong> of{" "}
                      <strong>{vouchers.length}</strong> vouchers
                      {costCenter !== ALL && " (matching lines only)"}
                    </span>
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={clearFilters}
                    >
                      <i className="fa fa-times me-1"></i>Clear filters
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Totals for the current selection */}
            <div className="row g-3 mb-4">
              <div className="col-lg-3 col-md-6">
                <div className="jv-stat">
                  <span className="jv-label">Vouchers</span>
                  <div className="jv-stat-value">{voucherCount}</div>
                  <small className="text-muted">
                    {lineCount} posting lines
                  </small>
                </div>
              </div>
              <div className="col-lg-3 col-md-6">
                <div className="jv-stat">
                  <span className="jv-label">Total Debit</span>
                  <div className="jv-stat-value">৳ {money(debitTotal)}</div>
                  <small className="text-muted">Sum of all debit lines</small>
                </div>
              </div>
              <div className="col-lg-3 col-md-6">
                <div className="jv-stat">
                  <span className="jv-label">Total Credit</span>
                  <div className="jv-stat-value">৳ {money(creditTotal)}</div>
                  <small className="text-muted">Sum of all credit lines</small>
                </div>
              </div>
              <div className="col-lg-3 col-md-6">
                <div
                  className="jv-stat"
                  style={{
                    borderLeftColor: balanced
                      ? GREEN
                      : partialSelection
                      ? NAVY
                      : RED,
                  }}
                >
                  <span className="jv-label">
                    {partialSelection && !balanced ? "Net" : "Balance"}
                  </span>
                  <div
                    className="jv-stat-value"
                    style={{
                      color: balanced ? GREEN : partialSelection ? NAVY : RED,
                    }}
                  >
                    {balanced ? "Balanced" : "৳ " + money(Math.abs(difference))}
                  </div>
                  <small className="text-muted">
                    {balanced
                      ? "Debits equal credits"
                      : partialSelection
                      ? "Expected: only part of each voucher is shown"
                      : "Debits and credits do not match"}
                  </small>
                </div>
              </div>
            </div>

            {/* Postings */}
            <div className="jv-panel">
              <div className="jv-panel-head">
                <span>
                  <i className="fa fa-table me-2"></i>Postings
                </span>
                <span className="jv-head-note">
                  {fromDate} &rarr; {toDate}
                </span>
              </div>
              <div className="jv-panel-body jv-table">
                <ReactDataTable
                  cols={cols}
                  receiveData={rows}
                  isLoading={loading}
                  actionTemplate={actionTemplate}
                  deleteData={deleteData}
                  rowClassName={(row) =>
                    row.isVoucherHead ? "jv-row-head" : "jv-row-line"
                  }
                  scrollHeight="560px"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Actions belong to the voucher rather than the line, so they sit on the head
// row.
const actionTemplate = (rowData, deleteData) => {
  if (!rowData.isVoucherHead) return null;

  return (
    <div className="d-flex gap-1 text-nowrap">
      <Link
        to={{
          pathname: "/journal-voucher-details/" + rowData.journal_voucher_id,
        }}
        className="btn btn-outline-secondary btn-sm"
        title="View voucher"
      >
        <i className="fa fa-file-text-o" aria-hidden="true"></i>
      </Link>
      <Link
        to={{
          pathname: "/edit-journal-voucher/" + rowData.journal_voucher_id,
        }}
        className="btn btn-outline-secondary btn-sm"
        title="Edit voucher"
      >
        <i className="fa fa-pencil" aria-hidden="true"></i>
      </Link>
      <button
        className="btn btn-outline-danger btn-sm"
        onClick={() =>
          deleteData(
            rowData.journal_voucher_id,
            "journal-voucher",
            "Journal Voucher"
          )
        }
        title="Delete voucher"
      >
        <i className="fa fa-trash"></i>
      </button>
    </div>
  );
};

export default List;
